/**
 * Pixel-art canvas helper.
 *
 * The game draws at a native resolution and the canvas is scaled up by a
 * whole-number factor only. Non-integer scaling or image smoothing would blur
 * the sprites, so both are ruled out here and this module is the only place
 * that touches canvas sizing.
 *
 * The scaling is done by the canvas rather than by the element: the backing
 * store is the native size times `render`, and the context carries a matching
 * transform, so every draw still speaks in native units and a sprite at whole
 * native coordinates still lands exactly on device pixels. What it buys is
 * that something which does not have to sit on the grid — the parallax
 * background — can be placed on a fraction of a native pixel. Moving a layer
 * one whole native pixel at a time steps about twice a second and reads as a
 * lurch; `step` is the smallest distance that can actually be drawn.
 *
 * Both native dimensions follow the panel so the scene fills it edge to edge.
 * fit() picks the largest scale that still leaves MIN_NATIVE_W native pixels
 * across and gives the scene whatever width remains at that scale. When the
 * panel has a fixed height (desktop, flagged by --fill-height: 1 and sized by
 * matchPanelHeights in main.ts) the height fills it too; otherwise (phones,
 * where the panel's height would just be the canvas's own) it follows the
 * width at a fixed aspect.
 */

/**
 * The narrowest scene worth drawing: the 96 px nest with room for a 32 px
 * dinosaur either side. Kept low so a narrowing panel stays at 2x with a
 * tighter yard rather than dropping to 1x and halving the whole world.
 */
export const MIN_NATIVE_W = 168;
/** 2x everywhere on desktop; a taller panel gets more sky, not chunkier pixels. */
export const MAX_SCALE = 2;
/** Native height as a fraction of native width when not filling a fixed panel: 4:3 plus a quarter more for the deeper ground. */
const ASPECT = (3 / 4) * 1.25;
/**
 * When the panel's height is fixed it is set from the side panel, which does
 * not shrink with the window while the yard's width does. Below this ratio
 * of height to width the window is too narrow for that height and the panel
 * is capped instead (matchPanelHeights in main.ts): a squarer yard would be
 * nothing but more sky.
 */
export const MAX_FILL_ASPECT = 3 / 4;
export const MIN_NATIVE_H = 210;
/** Sanity cap; in practice the panel height decides. */
export const MAX_NATIVE_H = 1200;

export interface PixelCanvas {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  /** Current native size in pixels; both change with the viewport. */
  width: number;
  height: number;
  /** Current integer scale factor (native pixels → CSS pixels). */
  scale: number;
  /** Backing-store pixels per native pixel: the scale times the display's. */
  render: number;
  /** The smallest native distance that lands on its own backing pixel. */
  step: number;
  /**
   * Recompute scale and native width so the canvas spans its parent. Returns
   * true when the size changed, which cleared the canvas: the caller should
   * redraw at once rather than leave a blank frame.
   */
  fit(): boolean;
  /** Convert a pointer event's client coordinates to native pixel coordinates. */
  toNative(clientX: number, clientY: number): { x: number; y: number };
}

export function createPixelCanvas(canvas: HTMLCanvasElement): PixelCanvas {
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) {
    throw new Error('2D canvas context is not available');
  }

  const pc: PixelCanvas = {
    canvas,
    ctx,
    width: MIN_NATIVE_W,
    height: MIN_NATIVE_H,
    scale: 1,
    render: 1,
    step: 1,
    fit() {
      const parent = canvas.parentElement;
      // clientWidth includes the panel's padding; the 24 takes it back off.
      const availW = parent ? parent.clientWidth - 24 : MIN_NATIVE_W;
      const fillsHeight =
        parent !== null &&
        getComputedStyle(parent).getPropertyValue('--fill-height').trim() === '1';
      const availH = fillsHeight && parent ? parent.clientHeight - 24 : Infinity;
      // The largest scale that leaves a usable scene in both directions.
      let s = 1;
      for (let candidate = MAX_SCALE; candidate > 1; candidate--) {
        if (
          Math.floor(availW / candidate) >= MIN_NATIVE_W &&
          Math.floor(availH / candidate) >= MIN_NATIVE_H
        ) {
          s = candidate;
          break;
        }
      }
      const w = Math.max(1, Math.floor(availW / s));
      const wanted = fillsHeight ? Math.floor(availH / s) : Math.round(w * ASPECT);
      const h = Math.min(MAX_NATIVE_H, Math.max(MIN_NATIVE_H, wanted));
      // Floored, never rounded: a backing store larger than the display has
      // to be scaled down to fit, and a pixel-art canvas scaled down aliases.
      const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
      const render = s * dpr;
      // Nothing moved: leave the canvas alone. Setting its size clears it,
      // and a phone fires resize every time the address bar slides in or out
      // while scrolling, so refitting regardless blanked the yard for a frame
      // each time — the yard flashed as the page scrolled.
      if (
        w === pc.width &&
        h === pc.height &&
        s === pc.scale &&
        render === pc.render &&
        canvas.width === w * render &&
        canvas.height === h * render
      ) {
        return false;
      }
      pc.scale = s;
      pc.render = render;
      pc.step = 1 / render;
      pc.width = w;
      pc.height = h;
      // Setting the size clears the canvas and resets context state; the
      // next frame redraws everything, and smoothing is reasserted below.
      canvas.width = w * render;
      canvas.height = h * render;
      canvas.style.width = `${w * s}px`;
      canvas.style.height = `${h * s}px`;
      // The stylesheet's minimum only holds the space until this first fit
      // (main.css, #nest); left on, it could stretch the canvas off whole
      // pixels.
      canvas.style.minHeight = '0';
      document.documentElement.style.setProperty('--scale', String(s));
      // Every draw keeps its native units; the transform does the scaling.
      ctx.setTransform(render, 0, 0, render, 0, 0);
      ctx.imageSmoothingEnabled = false;
      return true;
    },
    toNative(clientX, clientY) {
      const r = canvas.getBoundingClientRect();
      return {
        x: Math.floor((clientX - r.left) / pc.scale),
        y: Math.floor((clientY - r.top) / pc.scale),
      };
    },
  };

  pc.fit();
  // No resize listener here: the page owns refitting (main.ts), so it can
  // lay out the panels first and redraw straight after.
  return pc;
}
